import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { useReplyTicket, useTicket } from '../api/hooks';
import { Chip, Skeleton, TextInput, Button } from '../components/ui';
import { getTg } from '../lib/tg';
import { useT, useLocale } from '../i18n';

export function TicketDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const locale = useLocale();
  const ticket = useTicket(id);
  const reply = useReplyTicket(id);
  const [body, setBody] = useState('');

  const data = (ticket.data as Record<string, unknown> | undefined) ?? {};
  const inner = (data.data as Record<string, unknown> | undefined) ?? data;
  const messages = extractMessages(inner);

  return (
    <>
      <header style={{ padding: '12px 8px 0' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '8px 12px',
            color: 'var(--tg-button)',
            fontSize: 15,
            fontWeight: 500,
          }}
        >
          <ArrowLeft size={20} strokeWidth={2.4} />
          {t('common.back')}
        </button>
      </header>

      {/* Header card with title + inline status chip */}
      <div style={{ padding: '4px 16px 0' }}>
        <div
          style={{
            background: 'var(--tg-section-bg)',
            borderRadius: 'var(--r-lg)',
            padding: '16px 18px',
            boxShadow: 'var(--sh-1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            <h1
              style={{
                margin: 0,
                flex: 1,
                minWidth: 0,
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: '-0.01em',
                lineHeight: 1.35,
                color: 'var(--tg-text)',
                wordBreak: 'break-word',
              }}
            >
              {ticket.isLoading ? (
                <Skeleton width="70%" height={22} />
              ) : (
                String(inner.subject ?? inner.title ?? t('ticket.detail.fallbackTitle'))
              )}
            </h1>
            {!ticket.isLoading && (
              <div style={{ flexShrink: 0, marginTop: 1 }}>
                <TicketStatusChip status={inner.status} />
              </div>
            )}
          </div>
          {!ticket.isLoading &&
            typeof inner.department === 'object' &&
            inner.department && (
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--tg-hint)' }}>
                {(() => {
                  const d = inner.department as Record<string, unknown>;
                  const display = typeof d.display === 'string' ? d.display : '';
                  const code = typeof d.code === 'string' ? d.code : '';
                  if (display) return display;
                  if (code) {
                    const key = `ticket.dept.${code}` as Parameters<typeof t>[0];
                    const out = t(key);
                    return out === key ? code : out;
                  }
                  return '';
                })()}
              </div>
            )}
        </div>
      </div>

      {/* Messages — chat-bubble style */}
      <div
        style={{
          padding: '14px 12px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {ticket.isLoading && (
          <>
            <Skeleton height={56} radius={14} />
            <Skeleton height={56} radius={14} />
          </>
        )}
        {!ticket.isLoading && messages.length === 0 && (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: 'var(--tg-hint)',
              fontSize: 14,
            }}
          >
            {t('ticket.noMessages')}
          </div>
        )}
        {messages.map((m, idx) => {
          const user = (m.user ?? {}) as Record<string, unknown>;
          const userType = String(user.type ?? '').toLowerCase();
          const usernameRaw = String(user.username ?? '');
          const username = remapSpeaker(usernameRaw, t);
          const isAction = userType === 'system' || m.type === 'action';
          const isMe = userType === 'user';
          const isStaff =
            userType === 'admin' ||
            userType === 'staff' ||
            (!isMe && !isAction && (usernameRaw === '系统' || usernameRaw === 'system'));
          const msg = (m.message ?? {}) as Record<string, unknown>;
          const content = String(msg.content ?? m.content ?? '');
          const action = m.action as Record<string, unknown> | undefined;
          const actionType = action?.type ? String(action.type) : '';

          if (isAction) {
            return (
              <div
                key={String(m.ticket_update_id ?? m.id ?? idx)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  margin: '4px 0',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    background: 'var(--tg-secondary-bg)',
                    color: 'var(--tg-hint)',
                    borderRadius: 999,
                    padding: '4px 12px',
                    fontSize: 12,
                  }}
                >
                  {translateAction(content || actionType, t)}
                </span>
                <span style={{ fontSize: 10.5, color: 'var(--tg-hint)', opacity: 0.7 }}>
                  {fmtTime(m.created_at, locale)}
                </span>
              </div>
            );
          }

          const speaker = isStaff
            ? username || t('ticket.from.staff')
            : isMe
              ? username || t('ticket.from.you')
              : username || '—';

          return (
            <div
              key={String(m.ticket_update_id ?? m.id ?? idx)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMe ? 'flex-end' : 'flex-start',
                gap: 4,
                padding: '0 4px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  flexDirection: isMe ? 'row-reverse' : 'row',
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: isMe ? 'var(--brand)' : 'var(--g-700)',
                  }}
                >
                  {speaker}
                </span>
                <span style={{ fontSize: 11, color: 'var(--tg-hint)' }}>
                  {fmtTime(m.created_at, locale)}
                </span>
              </div>
              <div
                style={{
                  maxWidth: '85%',
                  background: isMe ? 'var(--brand)' : 'var(--tg-section-bg)',
                  color: isMe ? '#fff' : 'var(--tg-text)',
                  border: isMe ? 'none' : '0.5px solid var(--tg-separator)',
                  borderRadius: 14,
                  borderTopLeftRadius: isMe ? 14 : 4,
                  borderTopRightRadius: isMe ? 4 : 14,
                  padding: '10px 14px',
                  fontSize: 14.5,
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  boxShadow: 'var(--sh-1)',
                }}
              >
                {content || <span style={{ opacity: 0.6 }}>—</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Spacer so messages aren't hidden under the fixed composer */}
      <div style={{ height: 88 }} />

      {/* Reply composer — fixed bottom bar */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '10px 12px calc(var(--safe-bottom) + 10px)',
          background: 'var(--tg-section-bg)',
          borderTop: '0.5px solid var(--tg-separator)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          zIndex: 20,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
          }}
        >
          <div style={{ flex: 1 }}>
            <TextInput value={body} onChange={setBody} placeholder={t('ticket.replyPlaceholder')} />
          </div>
          <Button
            disabled={body.trim().length === 0 || reply.isPending}
            loading={reply.isPending}
            leading={!reply.isPending && <Send size={16} />}
            onClick={() => {
              reply.mutate(body.trim(), {
                onSuccess: () => {
                  setBody('');
                  getTg()?.HapticFeedback.notificationOccurred('success');
                },
                onError: () => getTg()?.HapticFeedback.notificationOccurred('error'),
              });
            }}
          >
            {t('ticket.replySubmit')}
          </Button>
        </div>
      </div>
    </>
  );
}

function extractMessages(inner: Record<string, unknown>): Array<Record<string, unknown>> {
  const list = inner.updates ?? inner.messages ?? inner.replies ?? inner.posts ?? inner.list ?? [];
  return Array.isArray(list) ? (list as Array<Record<string, unknown>>) : [];
}

function TicketStatusChip({ status }: { status: unknown }) {
  let code = '';
  let display = '';
  if (typeof status === 'string') {
    code = display = status;
  } else if (status && typeof status === 'object') {
    const o = status as Record<string, unknown>;
    code = String(o.code ?? '');
    display = String(o.display ?? o.code ?? '');
  }
  const c =
    code === 'open' || code === 'pending' || code === 'awaiting_reply'
      ? 'warning'
      : code === 'answered' || code === 'replied'
        ? 'success'
        : code === 'closed' || code === 'resolved'
          ? 'neutral'
          : 'brand';
  return <Chip color={c as 'warning' | 'success' | 'neutral' | 'brand'}>{display || '—'}</Chip>;
}

/** Map system-like usernames to a friendlier i18n label. */
function remapSpeaker(username: string, t: ReturnType<typeof useT>): string {
  const lo = username.toLowerCase();
  if (username === '系统' || lo === 'system') return t('ticket.from.staff');
  if (lo === 'ai_assistant' || lo === 'ai-assistant' || lo === 'ai assistant')
    return t('ticket.from.ai');
  return username;
}

/** Translate the raw system-action verb to a friendlier i18n label. */
function translateAction(s: string, t: ReturnType<typeof useT>): string {
  if (!s) return '';
  const key = `ticket.action.${s}` as Parameters<typeof t>[0];
  // Cast through `as` so unknown verbs fall back to the raw string.
  const out = t(key);
  return out === key ? s : out;
}

function fmtTime(v: unknown, locale: string): string {
  if (typeof v !== 'string') return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleString(locale === 'zh' ? 'zh-CN' : 'en-US');
}
